import React, { useEffect, useRef } from 'react';

import _ from 'lodash';
import * as Yup from 'yup';
import { Dialog, DialogContent, Grid, MenuItem, TextField } from '@material-ui/core';

import { DialogActions, DialogTitle, Form, Select } from 'components';
import { usePropsCallback, useValueChange } from 'hooks';
import { call, randomKey } from 'utils';
import helpers, { types } from './helpers';

import styles from './EditMessageDialog.module.scss';

const initialValues = {
  key: '',
  type: 'text',
  body: helpers.text.value,
  seconds: 1,
};

const validate = Yup.object().shape({
  key: Yup.string().required('Required'),
  type: Yup.string().required('Required').oneOf(types),
  body: Yup.object().when('type', (type, schema) => {
    const shape = (helpers[type] || {}).validate;
    return shape ? schema.shape(shape) : schema;
  }),
  seconds: Yup.number(),
});

const AutoClearBody = ({ form }) => {
  const setValue = usePropsCallback(value => {
    if (form.status === 'active') {
      if (value) {
        form.setFieldValue('body', value);
      }
    } else {
      form.setStatus('active');
    }
  });

  useEffect(() => {
    setValue((helpers[form.values.type] || {}).value);
  }, [form.values.type, setValue]);

  return null;
};

const EditMessageDialog = ({ open, data, onClose, onChange }) => {
  const session = useRef({ key: randomKey(10) });

  useValueChange(open, () => {
    if (open) {
      session.current.key = _.get(data, 'key') || randomKey(10);
      session.current.data = data;
    } else {
      session.current.key = randomKey(10);
    }
  });

  const renderDialog = form => {
    const { values, fieldProps, isSubmitting, submitForm } = form;

    if (!values.key) {
      form.setFieldValue('key', session.current.key);
      return null;
    }

    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle onClose={onClose}>
          {session.current.data ? 'Edit Message' : 'Add Message'}
        </DialogTitle>

        <DialogContent className={styles.content}>
          <input type="hidden" name="key" value={values.key} />

          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Select {...fieldProps('type')}>
                  {types.map(it => (
                    <MenuItem key={it} value={it}>
                      {_.capitalize(it)}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...fieldProps('seconds')}
                  type="number"
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                {call((helpers[values.type] || {}).renderInput, form) || null}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          actions={[
            { label: 'cancel', onClick: onClose, disabled: isSubmitting },
            {
              label: session.current.data ? 'update' : 'add',
              onClick: submitForm,
              disabled: isSubmitting,
              type: 'submit',
            },
          ]}
        />

        <AutoClearBody form={form} />
      </Dialog>
    );
  };

  return (
    <Form
      validationSchema={validate}
      initialValues={session.current.data || initialValues}
      onSubmit={values => call(onChange, values)}
      session={session.current.key}
    >
      {renderDialog}
    </Form>
  );
};

export default EditMessageDialog;
